import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"
import { scrollSheetToTop } from "../utils/sheet_scroll"

/**
 * Multi-step wizard controller for the event creation form.
 *
 * Manages step navigation (next/back/jump), stepper state (done/active/pending),
 * and footer button visibility.
 *
 * All navigation is client-side: the wizard form contains all steps in hidden
 * divs; showing/hiding is driven by toggling the `hidden` class. The form
 * submits via the existing create/update action.
 *
 * Usage:
 *   <form data-controller="event-wizard" data-event-wizard-total-steps-value="4">
 *     ... steps, stepper, footer ...
 *   </form>
 */
export default class extends Controller {
  static values = {
    totalSteps: { type: Number, default: 4 },
    errorSteps: { type: String, default: "" },
  }

  static targets = [
    // Step containers
    "step",
    "stepContainer",
    "container",
    // Progress segments
    "stepper",
    "stepSegment",
    "stepCounter",
    // Footer buttons
    "backButton",
    "continueButton",
    "forwardButtons",
    "saveButton",
    // Form inputs
    "publishInput",
    "nameInput",
    "startsAtInput",
  ]

  declare readonly hasPublishInputTarget: boolean
  declare readonly publishInputTarget: HTMLInputElement
  declare totalStepsValue: number
  declare errorStepsValue: string
  declare readonly stepTargets: HTMLElement[]
  declare readonly stepSegmentTargets: HTMLElement[]
  declare readonly hasStepperTarget: boolean
  declare readonly stepperTarget: HTMLElement
  declare readonly hasStepCounterTarget: boolean
  declare readonly stepCounterTarget: HTMLElement
  declare readonly hasBackButtonTarget: boolean
  declare readonly backButtonTarget: HTMLElement
  declare readonly hasContinueButtonTarget: boolean
  declare readonly continueButtonTarget: HTMLElement
  declare readonly hasSaveButtonTarget: boolean
  declare readonly saveButtonTarget: HTMLElement
  declare readonly hasNameInputTarget: boolean
  declare readonly nameInputTarget: HTMLInputElement
  declare readonly descriptionInputTarget: HTMLTextAreaElement
  declare readonly eventTypeInputTargets: HTMLInputElement[]
  declare readonly hasStartsAtInputTarget: boolean
  declare readonly startsAtInputTarget: HTMLInputElement
  declare readonly endsAtInputTarget: HTMLInputElement
  declare readonly locationInputTargets: HTMLInputElement[]
  declare readonly capacityInputTarget: HTMLInputElement

  private currentStep = 1

  connect() {
    // On a 422 re-render the server tags the wizard with the steps that own
    // the failing fields. Open the first of them so the user does not have to
    // hunt for the bad input.
    this.showStep(this.firstErrorStep() ?? 1)
  }

  /**
   * Advance to the next step. Validates the current step first.
   */
  next() {
    if (this.currentStep >= this.totalStepsValue) return

    if (!this.validateCurrentStep()) return

    this.showStep(this.currentStep + 1)
  }

  /**
   * Go back to the previous step.
   */
  back() {
    if (this.currentStep <= 1) return
    this.showStep(this.currentStep - 1)
  }

  // ── Private methods ──────────────────────────────────────────

  // Parse the comma-separated server-set error-steps marker and return the
  // smallest in-range step number, or null when there are no errors.
  private firstErrorStep(): number | null {
    if (!this.errorStepsValue) return null
    const candidates = this.errorStepsValue
      .split(",")
      .map(token => parseInt(token.trim(), 10))
      .filter(n => Number.isInteger(n) && n >= 1 && n <= this.totalStepsValue)
    if (candidates.length === 0) return null
    return Math.min(...candidates)
  }

  private showStep(stepNumber: number) {
    this.currentStep = stepNumber

    // Show/hide step containers
    this.stepTargets.forEach(el => {
      const elStep = parseInt(el.dataset.wizardStep || "0", 10)
      el.classList.toggle("hidden", elStep !== stepNumber)
    })

    this.updateStepper()
    this.updateFooter()


    // Every step shares the sheet's one scroll container, so without this a
    // step opens at the offset the previous step was left at — tapping Continue
    // from the bottom of step 3 would land you mid-way down step 4. Only the
    // transitions reach here (connect / next / back / stepper click), never a
    // keystroke, which is what makes resetting safe.
    scrollSheetToTop(this.element)
  }

  // A segment is filled once you have reached it, so the bar reads as "how far
  // along" rather than "which ones are finished" — the current step counts.
  private updateStepper() {
    this.stepSegmentTargets.forEach((segment, idx) => {
      const stepNum = idx + 1
      const reached = stepNum <= this.currentStep
      segment.classList.toggle("bg-primary", reached)
      segment.classList.toggle("bg-base-200", !reached)
    })

    if (this.hasStepperTarget) {
      this.stepperTarget.setAttribute("aria-valuenow", String(this.currentStep))
    }

    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = i18n.t("events.wizard.step_indicator", {
        step: this.currentStep,
        total: this.totalStepsValue,
      })
    }
  }

  // Mirrors the referral wizard: a ghost Back that only appears once there is
  // somewhere to go back to, and one primary that fills the rest of the row.
  // The labels are static — "Continue: Location" restated the stepper.
  private updateFooter() {
    const isFirst = this.currentStep === 1
    const isLast = this.currentStep === this.totalStepsValue

    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.toggle("hidden", isFirst)
    }

    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.classList.toggle("hidden", isLast)
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", !isLast)
    }
  }


  private validateCurrentStep(): boolean {
    // Step 1: name is required
    if (this.currentStep === 1) {
      if (this.hasNameInputTarget && !this.nameInputTarget.value.trim()) {
        this.nameInputTarget.classList.add("input-error")
        this.nameInputTarget.focus()
        return false
      }
      this.nameInputTarget?.classList.remove("input-error")
    }

    // Step 2: starts_at is required
    if (this.currentStep === 2) {
      if (this.hasStartsAtInputTarget && !this.startsAtInputTarget.value) {
        this.startsAtInputTarget.classList.add("input-error")
        this.startsAtInputTarget.focus()
        return false
      }
      this.startsAtInputTarget?.classList.remove("input-error")
    }

    return true
  }





  // The wizard only ever saves a draft. Publishing is a decision made on the
  // event's own page, once its occurrences can be seen.
  /**
   * The footer's two final actions post the same form and differ only in
   * whether the event goes live. The buttons stay `type="button"` — a submit
   * button anywhere in the form would let Enter submit it from step 1 — so the
   * one that was clicked says which it is through an action param, and this
   * writes it to the hidden field the server reads.
   */
  submit(event: { params?: { publish?: unknown } }) {
    if (this.hasPublishInputTarget) {
      this.publishInputTarget.value = event?.params?.publish ? "1" : "0"
    }
    this.formElement.requestSubmit()
  }

  private get formElement(): HTMLFormElement {
    return this.element as HTMLFormElement
  }
}
